/**
 * 角色模板管理 API
 * 对应 foundation-service (7090)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type {
  RoleTemplate,
  RoleTemplateDetail,
  RoleTemplateQueryParams,
  RoleTemplateFormData,
  RoleTemplateUpdateData,
  ApplyTemplateData,
  ApplyTemplatesData,
  TenantInitData,
  TenantInitResult,
  Role,
} from './types';

/** 获取角色模板列表 */
export const getRoleTemplateList = async (params: RoleTemplateQueryParams = {}) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').get<{ data: RoleTemplate[]; count: number }>(
      '/role-templates',
      { params }
    )
  );
};

/** 获取可用模板列表（供选择器使用） */
export const getAvailableTemplates = async () => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').get<{ data: RoleTemplate[]; count: number }>(
      '/role-templates/available'
    )
  );
};

/** 获取角色模板详情 */
export const getRoleTemplateById = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').get<{ data: RoleTemplateDetail; count: number }>(
      `/role-templates/${id}`
    )
  );
};

/** 创建角色模板 */
export const createRoleTemplate = async (data: RoleTemplateFormData) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').post<{ data: RoleTemplate; count: number }>(
      '/role-templates',
      data
    )
  );
};

/** 更新角色模板 */
export const updateRoleTemplate = async (id: number, data: RoleTemplateUpdateData) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').put<{ data: null; count: number }>(
      `/role-templates/${id}`,
      data
    )
  );
};

/** 删除角色模板 */
export const deleteRoleTemplate = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').delete<{ data: null; count: number }>(
      `/role-templates/${id}`
    )
  );
};

/** 获取模板权限 */
export const getTemplatePermissions = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').get<{ data: { permissions: Array<{ id: number; name: string; code: string }>; permissionIds: number[] }; count: number }>(
      `/role-templates/${id}/permissions`
    )
  );
};

/** 分配模板权限 */
export const assignTemplatePermissions = async (id: number, permissionIds: number[]) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').put<{ data: null; count: number }>(
      `/role-templates/${id}/permissions`,
      { permission_ids: permissionIds }
    )
  );
};

/** 获取模板菜单 */
export const getTemplateMenus = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').get<{ data: { menus: Array<{ id: number; name: string; path: string }>; menuIds: number[] }; count: number }>(
      `/role-templates/${id}/menus`
    )
  );
};

/** 分配模板菜单 */
export const assignTemplateMenus = async (id: number, menuIds: number[]) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').put<{ data: null; count: number }>(
      `/role-templates/${id}/menus`,
      { menu_ids: menuIds }
    )
  );
};

/** 预览模板 */
export const previewTemplate = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').get<{ data: RoleTemplateDetail; count: number }>(
      `/role-templates/${id}/preview`
    )
  );
};

/** 应用模板（创建角色） */
export const applyTemplate = async (data: ApplyTemplateData) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').post<{ data: { roles: Role[] }; count: number }>(
      '/role-templates/apply',
      data
    )
  );
};

/** 批量应用模板 */
export const applyTemplates = async (data: ApplyTemplatesData) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').post<{ data: { roles: Role[] }; count: number }>(
      '/role-templates/apply-batch',
      data
    )
  );
};

/** 租户初始化（从模板创建默认角色） */
export const initTenant = async (data: TenantInitData = {}) => {
  return handleApiResponse(
    unifiedApi.service('roleTemplate').post<{ data: TenantInitResult; count: number }>(
      '/role-templates/init-tenant',
      data
    )
  );
};

/** 角色模板 API 对象 */
export const roleTemplateApi = {
  getRoleTemplateList,
  getAvailableTemplates,
  getRoleTemplateById,
  createRoleTemplate,
  updateRoleTemplate,
  deleteRoleTemplate,
  getTemplatePermissions,
  assignTemplatePermissions,
  getTemplateMenus,
  assignTemplateMenus,
  previewTemplate,
  applyTemplate,
  applyTemplates,
  initTenant,
};

export default roleTemplateApi;
